// Founding Cohort share card — DOM preview + canvas PNG download. No auto-posting.
import { useRef, useState } from "react";
import { Download, Share2 } from "lucide-react";
import { toast } from "sonner";

const CROWN_PATH = "M8 68 L16 24 L34 46 L50 14 L66 46 L84 24 L92 68 Z";

const drawCard = async ({ name, lane, memberNumber, cohortYear }) => {
  await document.fonts.ready;
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#0A0A0B";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, H * 0.4, 50, W / 2, H * 0.4, 700);
  glow.addColorStop(0, "rgba(201,162,39,0.12)");
  glow.addColorStop(1, "rgba(201,162,39,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = "rgba(201,162,39,0.6)";
  ctx.lineWidth = 3;
  ctx.strokeRect(40, 40, W - 80, H - 80);
  ctx.strokeStyle = "rgba(201,162,39,0.25)";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(58, 58, W - 116, H - 116);

  // crown
  ctx.save();
  ctx.translate(W / 2 - 55, 130);
  ctx.scale(1.1, 1.1);
  ctx.strokeStyle = "#E7C873";
  ctx.lineWidth = 4;
  ctx.lineJoin = "round";
  ctx.stroke(new Path2D(CROWN_PATH));
  ctx.fillStyle = "#C9A227";
  ctx.fillRect(8, 74, 84, 4);
  ctx.restore();

  ctx.textAlign = "center";
  ctx.fillStyle = "#C9A227";
  ctx.font = "600 30px Cinzel, serif";
  ctx.fillText("O F F I C I A L L Y   R E C R U I T E D", W / 2, 320);

  const metal = ctx.createLinearGradient(0, 380, 0, 620);
  metal.addColorStop(0, "#F7F8FA");
  metal.addColorStop(0.45, "#D4D7DD");
  metal.addColorStop(0.55, "#8E939C");
  metal.addColorStop(1, "#EDEFF3");
  ctx.fillStyle = metal;
  ctx.font = "120px Anton, Impact, sans-serif";
  ctx.fillText("BIG LITTLE", W / 2, 480);
  ctx.fillText("VILLAGE", W / 2, 610);

  ctx.fillStyle = "#E7C873";
  ctx.font = "600 40px Cinzel, serif";
  ctx.fillText(`F O U N D I N G   C O H O R T   ${cohortYear}`, W / 2, 700);

  const rule = ctx.createLinearGradient(W / 2 - 220, 0, W / 2 + 220, 0);
  rule.addColorStop(0, "rgba(201,162,39,0)");
  rule.addColorStop(0.5, "rgba(201,162,39,0.9)");
  rule.addColorStop(1, "rgba(201,162,39,0)");
  ctx.fillStyle = rule;
  ctx.fillRect(W / 2 - 220, 745, 440, 2);

  ctx.fillStyle = "#F4EFE6";
  ctx.font = "76px Anton, Impact, sans-serif";
  ctx.fillText((name || "FOUNDING CREATIVE").toUpperCase(), W / 2, 870);

  ctx.fillStyle = "#C9CCD3";
  ctx.font = "600 32px 'JetBrains Mono', monospace";
  ctx.fillText((lane || "CREATIVE COHORT MEMBER").toUpperCase(), W / 2, 940);

  ctx.fillStyle = "#C9A227";
  ctx.font = "700 36px 'JetBrains Mono', monospace";
  ctx.fillText(memberNumber ? `FOUNDING MEMBER \u2022 ${memberNumber}` : "FOUNDING MEMBER", W / 2, 1010);

  ctx.fillStyle = "rgba(201,162,39,0.7)";
  ctx.font = "600 26px Cinzel, serif";
  ctx.fillText("A   C R E A T I V E   E C O S Y S T E M", W / 2, 1220);

  return canvas;
};

export const ShareCard = ({ name, lane, memberNumber, cohortYear = "2027" }) => {
  const [busy, setBusy] = useState(false);
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const cardRef = useRef(null);

  const download = async () => {
    setBusy(true);
    try {
      const canvas = await drawCard({ name, lane, memberNumber, cohortYear });
      const a = document.createElement("a");
      a.download = "blv-founding-cohort-card.png";
      a.href = canvas.toDataURL("image/png");
      a.click();
      toast.success("Your Founding Cohort card is downloading ✦");
    } catch {
      toast.error("Couldn't build the card. Try a screenshot instead.");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    try {
      await navigator.share({
        title: "Big Little Village — Founding Cohort 2027",
        text: `Officially recruited. BIG LITTLE VILLAGE Founding Cohort ${cohortYear}. ✦`,
        url: window.location.origin,
      });
    } catch {}
  };

  return (
    <div data-testid="share-card">
      <div ref={cardRef} className="gold-frame grain-dark relative mx-auto aspect-[4/5] w-full max-w-xs bg-onyx p-6 text-center">
        <div className="pointer-events-none absolute inset-2 border border-gold/25" />
        <svg width="40" height="30" viewBox="0 0 100 80" fill="none" className="mx-auto mt-4" aria-hidden="true">
          <path d={CROWN_PATH} stroke="#E7C873" strokeWidth="5" strokeLinejoin="round" />
          <rect x="8" y="72" width="84" height="4" fill="#C9A227" />
        </svg>
        <p className="mt-4 font-cinzel text-[8px] uppercase tracking-[0.4em] text-gold">Officially recruited</p>
        <p className="mt-2 font-display text-3xl uppercase leading-[0.9] tracking-tight text-metallic">Big Little<br />Village</p>
        <p className="mt-2 font-cinzel text-[9px] uppercase tracking-[0.35em] text-goldlight">Founding Cohort {cohortYear}</p>
        <div className="gold-rule mx-auto mt-3 w-24" />
        <p className="mt-3 font-display text-lg uppercase tracking-tight text-cream" data-testid="share-card-name">{name || "Founding Creative"}</p>
        <p className="mt-1 font-mono text-[8px] uppercase tracking-[0.25em] text-silver">{lane || "Creative Cohort Member"}</p>
        <p className="mt-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-gold" data-testid="share-card-member">
          {memberNumber ? `Founding Member \u2022 ${memberNumber}` : "Founding Member"}
        </p>
      </div>
      <div className="mt-4 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <button
          onClick={download}
          disabled={busy}
          data-testid="download-card-btn"
          className="btn-sweep inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-6 py-3 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
        >
          <Download size={14} /> {busy ? "Building..." : "Get My Founding Cohort Card"}
        </button>
        {canShare && (
          <button
            onClick={share}
            data-testid="share-news-btn"
            className="inline-flex items-center gap-2 border border-silver/40 px-6 py-3 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight"
          >
            <Share2 size={14} /> Share the News
          </button>
        )}
      </div>
    </div>
  );
};
